// SEM-6250 — the diagram tab's Content (ADR-014 §2 *Content convention).
//
// Reuses the SAME query key as the chart tab (`["org", "chart", "live"]`), so
// switching tabs re-renders from cache instead of re-fetching a list the
// browser already holds. Two views of one dataset, one fetch.
//
// Pure, shell-agnostic. Solid-line tree only (ADR-012); the server scopes what
// is visible (admin /org vs read-only /me/org) — the menu is UX, never a trust
// boundary (ADR-008/ADR-017). No money.

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { Card, ErrorState, Loading } from "@novemai/components";

import { errorMessage } from "@/api/errors";
import { fetchOrgChart, type OrgChartResponse } from "./api";
import { OrgDiagram } from "./OrgDiagram";

export function OrgDiagramContent(): React.ReactElement {
  const { t } = useTranslation();

  const query = useQuery<OrgChartResponse>({
    queryKey: ["org", "chart", "live"],
    queryFn: () => fetchOrgChart(),
  });

  return (
    <div className="space-y-6">
      {query.isLoading ? (
        <Loading label={t("org.loading")} rows={6} />
      ) : query.isError ? (
        <ErrorState
          title={t("org.error_title")}
          description={errorMessage(query.error, t)}
        />
      ) : (
        <>
          <p className="font-serif text-sm italic text-[var(--fg-muted)]">
            {t("org.diagram_depth_note")}
          </p>
          <Card className="overflow-hidden p-0">
            <OrgDiagram
              nodes={query.data?.nodes ?? []}
              labels={{
                regionLabel: t("org.diagram_region"),
                expandLabel: t("org.chart_expand"),
                collapseLabel: t("org.chart_collapse"),
                noTitle: t("org.chart_no_title"),
                empty: t("org.chart_empty"),
                hiddenLabel: (count): string =>
                  t("org.diagram_hidden", { count }),
                zoomInLabel: t("org.diagram_zoom_in"),
                zoomOutLabel: t("org.diagram_zoom_out"),
                fitLabel: t("org.diagram_fit"),
                overviewLabel: t("org.diagram_overview"),
              }}
            />
          </Card>
        </>
      )}
    </div>
  );
}
